import { useState, useEffect, useCallback } from 'react';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { usePermissions } from '../context/PermissionsContext';
import {
  Plus, X, FloppyDisk, ArrowsClockwise, ShieldCheck, Lock,
  Check, ArrowCounterClockwise, Shield, CaretDown, CaretRight,
  PencilSimple,
} from '@phosphor-icons/react';
import { Button } from './../components/redesign/ui';
import { ROLE_LABELS } from '../utils/constants';
import { ALL_DEPT_OPTIONS, COMPLAINT_GROUPS } from '../constants/ticketOptions';
import toast from 'react-hot-toast';
import UsersView from './Users.view';

const ROLES = ['agent', 'dept_user', 'dept_manager', 'entity_manager', 'super_admin'];

const ROLE_DESCRIPTIONS = {
  agent:             'Can view and manage tickets for their entity.',
  dept_user:         'Department staff who receive escalated tickets and submit department responses.',
  dept_manager:      'Department manager (QC, Finance, Kitchen, etc.) with access to tickets and department responses.',
  entity_manager:    'Full access to their entity — tickets, users, and offers.',
  super_admin:       'Unrestricted access to all entities and settings.',
};

// users.department stores the display LABEL (Sprint 11.2). Backend normalises
// label → slug when matching escalated_to_dept.
const DEPT_OPTIONS = ['', ...ALL_DEPT_OPTIONS.map((d) => d.label)];

const ENTITY_OPTIONS = [
  { slug: 'abraaj-water',    label: 'Abraaj Water' },
  { slug: 'lofat-lifestyle', label: 'Lofat Lifestyle' },
  { slug: 'regency-kuwait',  label: 'Regency Kuwait' },
];

// Shared token-styled input/select class.
const INPUT = 'w-full rounded-field border border-paper-border2 bg-paper px-3 py-2 text-sm text-ink outline-none focus:border-ink';
const INPUT_ERR = 'w-full rounded-field border border-status-escalated bg-paper px-3 py-2 text-sm text-ink outline-none focus:border-ink';

// ── UserModal ───────────────────────────────────────────────────────────────
function UserModal({ user, entities, onSave, onClose, isSuperAdmin, lockedEntitySlug = null }) {
  const isNew = !user;
  // Derive initial entities array:
  //   0. If lockedEntitySlug (entity_manager caller) → force to that single slug
  //   1. Use user.entities if it's a non-empty array (modern users)
  //   2. Fall back to [entity_slug] if entity_slug is set (older users where entities = [])
  //   3. Empty array for new users
  const initEntities = (() => {
    if (lockedEntitySlug) return [lockedEntitySlug];
    if (Array.isArray(user?.entities) && user.entities.length > 0) return user.entities;
    if (user?.entity_slug) return [user.entity_slug];
    return [];
  })();

  const [form, setForm] = useState({
    name:              user?.name              || '',
    email:             user?.email             || '',
    role:              user?.role              || 'agent',
    is_active:         user?.is_active         ?? true,
    password:          '',
    complaint_groups:  Array.isArray(user?.complaint_groups) ? user.complaint_groups : [],
    department:        user?.department        || '',
    entities:          initEntities,
    can_manage_offers: user?.can_manage_offers === true,
  });
  const [saving,    setSaving]    = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const toggleGroup = (group) => {
    setForm((p) => {
      const cur = p.complaint_groups || [];
      const next = cur.includes(group) ? cur.filter((g) => g !== group) : [...cur, group];
      return { ...p, complaint_groups: next };
    });
  };

  const toggleEntity = (slug) => {
    setForm((p) => {
      const cur = p.entities || [];
      const next = cur.includes(slug) ? cur.filter((s) => s !== slug) : [...cur, slug];
      return { ...p, entities: next };
    });
  };

  // Derive entity_id: exactly one entity checked → that entity's id; else null.
  const deriveEntityId = () => {
    const checked = form.entities || [];
    if (checked.length === 1) return entities.find((e) => e.slug === checked[0])?.id || null;
    return null;
  };

  const showDeptField = ['dept_user', 'dept_manager', 'logistics_manager'].includes(form.role);
  const deptRequired  = form.role === 'dept_manager';

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) { toast.error('Name and email are required'); return; }
    if (isNew && !form.password)   { toast.error('Password is required for new users'); return; }
    if (deptRequired && !form.department) { toast.error('Department is required for Department Manager role'); return; }
    if ((form.entities || []).length === 0) { toast.error('Please select at least one entity.'); return; }
    setSaving(true);
    try {
      // Force can_manage_offers=false when role is super_admin/entity_manager —
      // those roles grant offers access implicitly, so the flag is meaningless
      // for them. Also Boolean-coerce to guard against form-library falsy strip.
      const isImplicitCapabilityRole = ['super_admin', 'entity_manager'].includes(form.role);
      const payload = {
        ...form,
        entity_id: deriveEntityId(),
        can_manage_offers: isImplicitCapabilityRole ? false : Boolean(form.can_manage_offers),
      };
      if (isNew) { await api.post('/users', payload); toast.success('User created'); }
      else       { await api.put(`/users/${user.id}`, payload); toast.success('User updated'); }
      onSave();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save user');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink/40 p-4 backdrop-blur-sm">
      <div className="flex max-h-[90vh] w-full max-w-lg flex-col rounded-card border border-paper-border bg-paper-panel shadow-soft">
        {/* Header */}
        <div className="flex flex-shrink-0 items-center justify-between border-b border-paper-border px-6 py-4">
          <h2 className="font-serif text-base text-ink">{isNew ? 'Add New User' : 'Edit User'}</h2>
          <button onClick={onClose} className="inline-flex h-8 w-8 items-center justify-center rounded-field text-ink-muted hover:bg-paper-muted">
            <X size={16} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex-1 space-y-5 overflow-y-auto p-6">
          {/* Row 1: Name + Email */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="mb-1 block text-xs text-ink-faint">Full Name *</label>
              <input type="text" value={form.name} onChange={(e) => set('name', e.target.value)} required
                placeholder="Full name" className={INPUT} dir="auto" />
            </div>
            <div>
              <label className="mb-1 block text-xs text-ink-faint">Email *</label>
              <input type="email" value={form.email} onChange={(e) => set('email', e.target.value)} required
                placeholder="email@example.com" className={INPUT} />
            </div>
          </div>

          {/* Row 2: Role */}
          <div>
            <label className="mb-1 block text-xs text-ink-faint">Role *</label>
            <select value={form.role} onChange={(e) => set('role', e.target.value)} className={INPUT}>
              {ROLES.filter((r) => isSuperAdmin || r !== 'super_admin').map((r) => (
                <option key={r} value={r}>{ROLE_LABELS[r]}</option>
              ))}
            </select>
            {ROLE_DESCRIPTIONS[form.role] && (
              <p className="mt-1.5 text-xs leading-snug text-ink-faint">{ROLE_DESCRIPTIONS[form.role]}</p>
            )}
          </div>

          {/* Row 3: Department (conditional) */}
          {showDeptField && (
            <div>
              <label className="mb-1 block text-xs text-ink-faint">
                Department {deptRequired && <span className="text-status-escalated">*</span>}
              </label>
              <select value={form.department} onChange={(e) => set('department', e.target.value)}
                className={deptRequired && !form.department ? INPUT_ERR : INPUT}>
                <option value="">— No Department —</option>
                {DEPT_OPTIONS.filter((d) => d !== '').map((d) => <option key={d} value={d}>{d}</option>)}
              </select>
              <p className="mt-1 text-xs text-ink-faint">
                {deptRequired
                  ? 'Required — determines which escalated tickets this manager oversees.'
                  : 'Determines which escalated tickets this user can respond to.'}
              </p>
            </div>
          )}

          <hr className="border-paper-border" />

          {/* Row 4: Entity Access — hidden when locked (entity_manager caller forces own entity) */}
          {!lockedEntitySlug && (
            <div>
              <label className="mb-2 block text-xs text-ink-faint">Entity Access *</label>
              <div className="flex flex-wrap gap-4 rounded-field border border-paper-border bg-paper-muted p-3">
                {ENTITY_OPTIONS.map((ent) => {
                  const checked = (form.entities || []).includes(ent.slug);
                  return (
                    <label key={ent.slug} className="flex cursor-pointer select-none items-center gap-2">
                      <input type="checkbox" checked={checked} onChange={() => toggleEntity(ent.slug)}
                        className="rounded border-paper-border2 text-status-under-process focus:ring-status-under-process" />
                      <span className="text-sm text-ink">{ent.label}</span>
                    </label>
                  );
                })}
              </div>
              {(form.entities || []).length === 0 && (
                <p className="mt-1 text-xs text-status-pending-finance">Please select at least one entity.</p>
              )}
            </div>
          )}

          <hr className="border-paper-border" />

          {/* Row 5: Notification Groups accordion */}
          <div>
            <button type="button" onClick={() => setNotifOpen((o) => !o)}
              className="group flex w-full items-center justify-between text-left">
              <span className="text-sm font-medium text-ink transition-colors group-hover:text-status-under-process">
                Advanced: Email Notification Groups
                {form.complaint_groups.length > 0 && (
                  <span className="ml-2 rounded-chip bg-status-under-process/8 px-2 py-0.5 text-xs font-normal text-status-under-process">
                    {form.complaint_groups.length} selected
                  </span>
                )}
              </span>
              {notifOpen ? <CaretDown size={14} className="text-ink-faint" /> : <CaretRight size={14} className="text-ink-faint" />}
            </button>
            {notifOpen && (
              <div className="mt-3">
                <div className="mb-2 flex items-center justify-between">
                  <p className="text-xs leading-snug text-ink-faint">
                    This user receives an email when a ticket is created matching any checked group.
                    Entity managers with no groups checked receive all tickets for their entity.
                  </p>
                  {form.complaint_groups.length > 0 && (
                    <button type="button" onClick={() => set('complaint_groups', [])}
                      className="ml-3 flex-shrink-0 text-xs text-ink-faint hover:text-status-escalated">
                      Clear all
                    </button>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 rounded-field border border-paper-border bg-paper-muted p-3">
                  {COMPLAINT_GROUPS.map((g) => {
                    const checked = (form.complaint_groups || []).includes(g.slug);
                    return (
                      <label key={g.slug} className="flex cursor-pointer select-none items-center gap-2">
                        <input type="checkbox" checked={checked} onChange={() => toggleGroup(g.slug)}
                          className="rounded border-paper-border2 text-status-under-process focus:ring-status-under-process" />
                        <span className="text-sm text-ink">{g.label}</span>
                      </label>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* Row 5b: Offers capability — only meaningful for worker roles;
              super_admin & entity_manager have offers access implicitly. */}
          {!['super_admin', 'entity_manager'].includes(form.role) && (
            <div className="rounded-field border border-paper-border bg-paper-muted p-3">
              <label className="flex cursor-pointer select-none items-center gap-2">
                <input type="checkbox" checked={!!form.can_manage_offers}
                  onChange={(e) => set('can_manage_offers', e.target.checked)}
                  className="rounded border-paper-border2 text-status-under-process focus:ring-status-under-process" />
                <span className="text-sm font-medium text-ink">Grant Offers &amp; Promos access</span>
              </label>
              <p className="mt-1 text-xs text-ink-faint">
                Lets this user edit offers alongside their base-role duties.
              </p>
            </div>
          )}

          <hr className="border-paper-border" />

          {/* Row 6: Password + Active */}
          <div className="grid grid-cols-2 items-end gap-4">
            <div>
              <label className="mb-1 block text-xs text-ink-faint">
                {isNew ? 'Password *' : 'New Password (leave blank to keep)'}
              </label>
              <input type="password" value={form.password} onChange={(e) => set('password', e.target.value)}
                required={isNew} placeholder="••••••••" className={INPUT} />
            </div>
            {!isNew && (
              <div className="flex items-center gap-3 pb-2">
                <input type="checkbox" id="is_active" checked={form.is_active}
                  onChange={(e) => set('is_active', e.target.checked)}
                  className="rounded border-paper-border2 text-status-under-process focus:ring-status-under-process" />
                <label htmlFor="is_active" className="text-sm font-medium text-ink">Active</label>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex justify-end gap-3 pt-1">
            <Button variant="secondary" type="button" onClick={onClose}>Cancel</Button>
            <Button variant="primary" type="submit" disabled={saving}>
              <FloppyDisk size={14} weight="fill" />
              {saving ? 'Saving…' : (isNew ? 'Create User' : 'Save Changes')}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Roles / Pages order for the matrix ──────────────────────────────────────
const MATRIX_ROLES = ['super_admin', 'entity_manager', 'agent', 'dept_user', 'dept_manager'];
const MATRIX_PAGES = ['dashboard', 'tickets', 'new_ticket', 'users', 'admin', 'offers'];

function PermissionsMatrix({ isSuperAdmin }) {
  const { permissions, updatePermissions, resetPermissions } = usePermissions();

  const [editing,   setEditing]   = useState(false);
  const [draft,     setDraft]     = useState(null);
  const [saving,    setSaving]    = useState(false);
  const [resetting, setResetting] = useState(false);

  const startEdit = useCallback(() => {
    const d = {};
    MATRIX_PAGES.forEach((page) => { d[page] = [...(permissions[page]?.allowedRoles || [])]; });
    setDraft(d);
    setEditing(true);
  }, [permissions]);

  const cancelEdit = () => { setDraft(null); setEditing(false); };

  const toggle = (page, role) => {
    const locked = permissions[page]?.lockedRoles || ['super_admin'];
    if (locked.includes(role)) return;
    setDraft((prev) => {
      const cur = prev[page] || [];
      const next = cur.includes(role) ? cur.filter((r) => r !== role) : [...cur, role];
      return { ...prev, [page]: next };
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {};
      MATRIX_PAGES.forEach((p) => { payload[p] = draft[p]; });
      await updatePermissions(payload);
      toast.success('Permissions saved');
      setEditing(false);
      setDraft(null);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save permissions');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!window.confirm('Reset all permissions to system defaults?')) return;
    setResetting(true);
    try {
      await resetPermissions();
      toast.success('Permissions reset to defaults');
    } catch (err) {
      toast.error('Failed to reset permissions');
    } finally {
      setResetting(false);
    }
  };

  const activePerms = editing ? draft : null;

  return (
    <div className="overflow-hidden rounded-card border border-paper-border bg-paper-panel shadow-soft">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-paper-border px-5 py-4">
        <div className="flex items-center gap-2">
          <Shield size={18} className="text-ink-muted" />
          <div>
            <h2 className="font-serif text-base text-ink">Role Permissions Matrix</h2>
            <p className="mt-0.5 text-xs text-ink-faint">Controls which pages each role can access</p>
            <p className="mt-1 text-xs text-ink-faint">
              Additional users can be granted Offers access via the toggle in the Users table above.
            </p>
          </div>
        </div>
        {isSuperAdmin && (
          <div className="flex items-center gap-2">
            {editing ? (
              <>
                <Button variant="secondary" onClick={cancelEdit}>Cancel</Button>
                <Button variant="primary" onClick={handleSave} disabled={saving}>
                  <FloppyDisk size={13} weight="fill" />
                  {saving ? 'Saving…' : 'Save Changes'}
                </Button>
              </>
            ) : (
              <>
                <Button variant="secondary" onClick={handleReset} disabled={resetting} title="Reset to system defaults">
                  <ArrowCounterClockwise size={13} className={resetting ? 'animate-spin' : ''} />
                  Reset
                </Button>
                <Button variant="primary" onClick={startEdit}>
                  <PencilSimple size={13} />
                  Edit
                </Button>
              </>
            )}
          </div>
        )}
      </div>

      {/* Matrix table */}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-paper-border bg-paper-muted/40">
              <th className="w-44 px-5 py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-ink-faint">
                Page
              </th>
              {MATRIX_ROLES.map((role) => (
                <th key={role} className="px-4 py-3 text-center text-[11px] font-semibold uppercase tracking-wider text-ink-faint">
                  <div className="flex flex-col items-center gap-1">
                    {role === 'super_admin' && <ShieldCheck size={13} weight="fill" className="text-status-under-process" />}
                    <span>{ROLE_LABELS[role] || role}</span>
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {MATRIX_PAGES.map((page) => {
              const pageMeta = permissions[page] || {};
              const locked   = pageMeta.lockedRoles  || ['super_admin'];
              const allowed  = editing ? (activePerms[page] || []) : (pageMeta.allowedRoles || []);
              return (
                <tr key={page} className="border-b border-paper-border hover:bg-paper-muted/50">
                  <td className="px-5 py-3 font-medium text-ink">{pageMeta.label || page}</td>
                  {MATRIX_ROLES.map((role) => {
                    const isLocked  = locked.includes(role);
                    const isChecked = allowed.includes(role);
                    const canToggle = editing && !isLocked;
                    return (
                      <td key={role} className="px-4 py-3 text-center">
                        {isLocked ? (
                          <span title="Locked — cannot be changed"
                            className="mx-auto inline-flex h-7 w-7 items-center justify-center rounded-full bg-paper-muted">
                            <Lock size={13} weight="fill" className={isChecked ? 'text-status-under-process' : 'text-ink-faint'} />
                          </span>
                        ) : canToggle ? (
                          <button onClick={() => toggle(page, role)}
                            className={`mx-auto inline-flex h-7 w-7 items-center justify-center rounded-full border-2 transition-colors ${
                              isChecked
                                ? 'border-status-under-process bg-status-under-process text-paper hover:bg-status-under-process/90'
                                : 'border-paper-border2 text-transparent hover:border-status-under-process'
                            }`}>
                            <Check size={13} weight="bold" />
                          </button>
                        ) : (
                          <span className="mx-auto inline-flex h-7 w-7 items-center justify-center">
                            {isChecked
                              ? <Check size={14} weight="bold" className="text-status-resolved" />
                              : <span className="block h-0.5 w-4 rounded-full bg-paper-border2" />}
                          </span>
                        )}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Legend */}
      <div className="flex items-center gap-5 border-t border-paper-border bg-paper-muted/40 px-5 py-3 text-xs text-ink-faint">
        <span className="flex items-center gap-1.5">
          <Lock size={12} weight="fill" className="text-status-under-process" />
          Locked (system enforced)
        </span>
        <span className="flex items-center gap-1.5">
          <Check size={12} weight="bold" className="text-status-resolved" />
          Access granted
        </span>
        {editing && (
          <span className="flex items-center gap-1.5 font-medium text-status-under-process">
            Click checkboxes to toggle access
          </span>
        )}
      </div>
    </div>
  );
}

// ── role summary tagline ────────────────────────────────────────────────────
// Aggregate counts by role from users array; join non-zero as "N Role(s)"
// segments separated by " · ". Empty string when no users.
function computeRoleSummary(users) {
  if (!users || users.length === 0) return '';
  const counts = {};
  for (const u of users) {
    const key = u.role || 'agent';
    counts[key] = (counts[key] || 0) + 1;
  }
  const parts = [];
  for (const [role, count] of Object.entries(counts)) {
    const label = ROLE_LABELS[role] || role;
    parts.push(`${count} ${label}${count > 1 ? 's' : ''}`);
  }
  return parts.join(' · ');
}

// ── container ───────────────────────────────────────────────────────────────
export default function Users() {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [entities, setEntities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null); // null | 'new' | user object

  const load = async () => {
    setLoading(true);
    try {
      const [usersRes, entitiesRes] = await Promise.all([
        api.get('/users'),
        api.get('/entities'),
      ]);
      setUsers(usersRes.data);
      setEntities(entitiesRes.data);
    } catch (err) {
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const deactivate = async (id) => {
    if (!window.confirm('Deactivate this user?')) return;
    try {
      await api.delete(`/users/${id}`);
      toast.success('User deactivated');
      load();
    } catch (err) {
      toast.error('Failed to deactivate user');
    }
  };

  const toggleOffers = async (u) => {
    const next = !u.can_manage_offers;
    try {
      await api.put(`/users/${u.id}`, { can_manage_offers: next });
      toast.success(next ? 'Offers access granted' : 'Offers access revoked');
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to update Offers access');
    }
  };

  const roleSummary = computeRoleSummary(users);
  // For entity_manager callers, force new/edited users into the caller's own
  // entity. Prefer entities JSONB [0]; fall back to entity_id → slug lookup.
  const callerEntitySlug =
    currentUser?.entities?.[0]
    || entities.find((e) => e.id === currentUser?.entity_id)?.slug
    || null;

  return (
    <>
      <UsersView
        users={users}
        entities={entities}
        loading={loading}
        currentUser={currentUser}
        roleSummary={roleSummary}
        onRefresh={load}
        onAddUser={() => setModal('new')}
        onEditUser={(u) => setModal(u)}
        onDeactivate={deactivate}
        onToggleOffers={toggleOffers}
      />
      {(currentUser?.role === 'super_admin' || currentUser?.role === 'entity_manager') && (
        <div className="mt-6 p-1">
          <PermissionsMatrix isSuperAdmin={currentUser?.role === 'super_admin'} />
        </div>
      )}
      {modal && (
        <UserModal
          user={modal === 'new' ? null : modal}
          entities={entities}
          isSuperAdmin={currentUser?.role === 'super_admin'}
          lockedEntitySlug={currentUser?.role === 'entity_manager' ? callerEntitySlug : null}
          onSave={() => { setModal(null); load(); }}
          onClose={() => setModal(null)}
        />
      )}
    </>
  );
}
